import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getDatabase, ref, onValue } from 'firebase/database';
import { Link } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import './AuditLogs.css';

export default function AuditLogs({ isSuperAdmin = false }) {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '', // Category filter only
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const logsPerPage = 10;
  // State for export category (Super Admin feature)
  const [exportCategory, setExportCategory] = useState('');

  // Function to determine role from event type or subcategory
  const getRoleFromLog = (log) => {
    const eventType = log.event_type?.toLowerCase() || '';
    const subCategory = log.subCategory?.toLowerCase() || '';
    
    // Check for technician patterns
    if (eventType.includes('_tec') || eventType.includes('_technician') || eventType.includes('_tech') ||
        subCategory.includes('_tec') || subCategory.includes('_technician') || subCategory.includes('_tech')) {
      return 'Technician';
    }
    
    // Check for admin patterns
    if (eventType.includes('_admin') || subCategory.includes('_admin')) {
      return 'Admin';
    }
    
    // Check for doctor patterns
    if (eventType.includes('_doctor') || subCategory.includes('_doctor')) {
      return 'Doctor';
    }
    
    return 'Unknown';
  };

  // Function to decrypt data
  const decrypt = (encryptedData) => {
    if (!encryptedData) return encryptedData;
    try {
      const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
      const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.warn("Error decrypting data:", error);
      return encryptedData;
    }
  };

  // Helper function to parse dates for filtering - handles multiple formats
  const parseDateForFiltering = (timestamp) => {
    if (!timestamp) return new Date(0); // Return epoch date for null/undefined
    // Handle numeric (milliseconds) and Firebase Timestamp objects
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    if (typeof timestamp === 'object') {
      if ('seconds' in timestamp) {
        return new Date(timestamp.seconds * 1000);
      }
      if ('_seconds' in timestamp) {
        return new Date(timestamp._seconds * 1000);
      }
    }
    
    try {
      // Try to parse as ISO string first
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
      
      // Handle UK format: "25/12/2023, 14:30:45"
      if (typeof timestamp === 'string' && timestamp.includes('/') && timestamp.includes(',')) {
        const parts = timestamp.split(', ');
        if (parts.length === 2) {
          const datePart = parts[0];
          const timePart = parts[1];
          const dateParts = datePart.split('/');
          if (dateParts.length === 3) {
            // Convert DD/MM/YYYY to YYYY-MM-DD
            const day = dateParts[0];
            const month = dateParts[1];
            const year = dateParts[2];
            const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}`;
            const parsedDate = new Date(isoDate);
            if (!isNaN(parsedDate.getTime())) {
              return parsedDate;
            }
          }
        }
      }
      
      // Handle other potential formats
      const fallbackDate = new Date(timestamp.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
      if (!isNaN(fallbackDate.getTime())) {
        return fallbackDate;
      }
      
      // If all parsing attempts fail, return epoch date
      return new Date(0);
    } catch (error) {
      console.warn('Error parsing date for filtering:', timestamp, error);
      return new Date(0);
    }
  };

  // Format timestamp to local date and time - handles multiple date formats
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    // Handle numeric & Firebase Timestamp objects
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleString();
    }
    if (typeof timestamp === 'object') {
      if ('seconds' in timestamp) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      if ('_seconds' in timestamp) {
        return new Date(timestamp._seconds * 1000).toLocaleString();
      }
    }
    
    try {
      // If it's already a formatted string (like from old logs), return as is
      if (typeof timestamp === 'string' && timestamp.includes(',')) {
        return timestamp;
      }
      
      // Try to parse as ISO string first
      const date = new Date(timestamp);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        // If invalid, try to parse different formats
        if (typeof timestamp === 'string') {
          // Handle UK format: "25/12/2023, 14:30:45"
          if (timestamp.includes('/') && timestamp.includes(',')) {
            const parts = timestamp.split(', ');
            if (parts.length === 2) {
              const datePart = parts[0];
              const timePart = parts[1];
              const dateParts = datePart.split('/');
              if (dateParts.length === 3) {
                // Convert DD/MM/YYYY to YYYY-MM-DD
                const day = dateParts[0];
                const month = dateParts[1];
                const year = dateParts[2];
                const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}`;
                const parsedDate = new Date(isoDate);
                if (!isNaN(parsedDate.getTime())) {
                  return parsedDate.toLocaleString();
                }
              }
            }
          }
          
          // Handle other potential formats
          const fallbackDate = new Date(timestamp.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
          if (!isNaN(fallbackDate.getTime())) {
            return fallbackDate.toLocaleString();
          }
        }
        
        // If all parsing attempts fail, return the original timestamp
        return timestamp;
      }
      
      // Valid date, format it
      return date.toLocaleString();
    } catch (error) {
      console.warn('Error formatting timestamp:', timestamp, error);
      return timestamp || 'Invalid Date';
    }
  };

  useEffect(() => {
    console.log('AuditLogs useEffect triggered');
    const db = getDatabase();
    const logsRef = ref(db, 'audit_logs');
    
    return onValue(logsRef, (snapshot) => {
      setLoading(true);
      const data = snapshot.val() || {};
      const allLogs = [];

      // Process UserManagement logs (handles nested Admin/Technician/Doctor logs)
      // --- Recursive traversal to collect ALL UserManagement logs (Admin / Technician / Doctor) ---
      if (data.UserManagement) {
        const traverseUserLogs = (node, path = []) => {
          Object.entries(node).forEach(([key, value]) => {
            if (!value || typeof value !== 'object') return;

            // Detect a leaf log entry (contains a timestamp field)
            if ('timestamp' in value || 'timeStamp' in value) {
              // Decrypt patient IDs if they exist
              let decryptedPatientId = '';
              if (value.patient_id_encrypted) {
                decryptedPatientId = decrypt(value.patient_id_encrypted);
              }

              // Derive role from the first segment in the path (Admin_logs / Technician_logs / Doctor_logs)
              const topLevel = path[0] || '';
              let derivedRole = 'Unknown';
              if (topLevel.toLowerCase().includes('admin')) derivedRole = 'Admin';
              else if (topLevel.toLowerCase().includes('tech')) derivedRole = 'Technician';
              else if (topLevel.toLowerCase().includes('doctor')) derivedRole = 'Doctor';

              const logEntry = {
                id: key,
                category: 'UserManagement',
                subCategory: path.slice(1).join('/') || topLevel || '', // exclude id level
                decryptedPatientId,
                ...value,
                role: derivedRole,
              };

              // Fallback to existing role detector for edge-cases
              logEntry.role = getRoleFromLog(logEntry) || logEntry.role;

              allLogs.push(logEntry);
            } else {
              // Recurse deeper
              traverseUserLogs(value, [...path, key]);
            }
          });
        };

        traverseUserLogs(data.UserManagement, []);
      }

      // Process Authentication logs
      if (data.Authentication) {
        Object.entries(data.Authentication).forEach(([subCat, logsObj]) => {
          if (typeof logsObj === 'object') {
            Object.entries(logsObj).forEach(([id, entry]) => {
              const logEntry = {
                id,
                category: 'Authentication',
                subCategory: subCat,
                ...entry
              };
              
              // Add role information to each log entry
              logEntry.role = getRoleFromLog(logEntry);
              
              allLogs.push(logEntry);
            });
          }
        });
      }

            // --- Traverse Upload_Events (plural) and Upload_Event (singular) ---
      const processUploadNode = (root, label="") => {
        if (!root) return;
        const traverseUploads = (node, path = []) => {
          Object.entries(node).forEach(([key, value]) => {
            if (!value || typeof value !== 'object') return;
            if ('timestamp' in value || 'timeStamp' in value) {
              const logEntry = {
                id: key,
                ...value,
                category: 'Upload_Events',
                subCategory: path.join('/') || label,
              };
              logEntry.role = getRoleFromLog(logEntry);
              allLogs.push(logEntry);
            } else {
              traverseUploads(value, [...path, key]);
            }
          });
        };
        traverseUploads(root, []);
      };

      // plural path
      processUploadNode(data.Upload_Events);
      // singular path used by new technician uploads
      processUploadNode(data.Upload_Event);


      console.log(`Total logs from audit_logs: ${allLogs.length}`);
      console.log('Audit logs breakdown:', {
        userManagement: allLogs.filter(log => log.category === 'UserManagement').length,
        authentication: allLogs.filter(log => log.category === 'Authentication').length,
        uploadEvents: allLogs.filter(log => log.category === 'Upload_Events').length
      });
      
      // Log detailed Upload Events info
      const uploadEvents = allLogs.filter(log => log.category === 'Upload_Events');
      console.log('Upload Events details:', uploadEvents.map(log => ({
        id: log.id,
        subCategory: log.subCategory,
        event_type: log.event_type,
        timestamp: log.timestamp
      })));
      
      setLogs(allLogs);
      setLoading(false);
    });
  }, []);



  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...logs];

    // Apply date filters - handle multiple date formats
    if (filters.startDate) {
      filtered = filtered.filter(log => {
        try {
          const logDate = parseDateForFiltering(log.timestamp ?? log.timeStamp ?? log.details?.timestamp ?? log.details?.timeStamp ?? log.details?.time ?? log.time ?? log.created_at);
          const startDate = new Date(filters.startDate);
          return logDate >= startDate;
        } catch (error) {
          return false; // Exclude logs with invalid dates from filtering
        }
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(log => {
        try {
          const logDate = parseDateForFiltering(log.timestamp ?? log.timeStamp ?? log.details?.timestamp ?? log.details?.timeStamp ?? log.details?.time ?? log.time ?? log.created_at);
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999); // Include the entire end date
          return logDate <= endDate;
        } catch (error) {
          return false; // Exclude logs with invalid dates from filtering
        }
      });
    }

    // Apply category filter
    if (filters.category) {
      const cat = filters.category.trim().toLowerCase();
      filtered = filtered.filter(log => (log.category || '').trim().toLowerCase() === cat);
    }

    // Apply sorting (case-insensitive & null-safe)
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle undefined/null values
      aValue = aValue ?? '';
      bValue = bValue ?? '';

      // Special handling for timestamp
      if (sortConfig.key === 'timestamp') {
        aValue = parseDateForFiltering(aValue);
        bValue = parseDateForFiltering(bValue);
      } else {
        // For strings, compare lowercase version
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLogs(filtered);
    setCurrentPage(1);
  }, [logs, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * logsPerPage,
    currentPage * logsPerPage
  );

  // Export handler 
  const handleExport = () => {
    const exportLogs = exportCategory
      ? logs.filter((log) => log.category === exportCategory)
      : logs;

    if (exportLogs.length === 0) {
      alert('No logs available for the selected category');
      return;
    }

    const data = exportLogs.map((log) => ({
      Timestamp: formatTimestamp(log.timestamp),
      Event_Type: log.event_type,
      Category: log.category,
      SubCategory: log.subCategory || '',

      User: log.created_by || log.uploaded_by || log.admin_name || '',
      PatientName: log.patient_name || '',
      Action: log.details?.action || '',
      Device: log.details?.device || '',
      Browser: log.details?.browser || '',
      IP: log.details?.ip || '',
      Location: log.details?.location || '',
      Status:
        log.success === undefined ? '' : log.success ? 'Success' : 'Failed',
      FailureReason: log.failure_reason || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AuditLogs');
    XLSX.writeFile(
      workbook,
      `AuditLogs_${exportCategory || 'All'}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  return (
    <div className="audit-logs-container">
      <div className="audit-logs-header">
        {!isSuperAdmin && (
          <Link to="/Admin?section=AssignDoctor" className="header-back-button">
            <i id="backIcon" className="fas fa-long-arrow-alt-left"></i>
          </Link>
        )}
        <h2>Audit Logs</h2>
      </div>
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            placeholder="Start Date"
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            placeholder="End Date"
          />
        </div>

        <div className="filter-group">
          <label>Category Filter:</label>
          <select
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
            className="category-filter-select"
          >
            <option value="">All Categories</option>
            <option value="UserManagement">User Management</option>
            <option value="Authentication">Authentication</option>
            <option value="Upload_Events">Upload Events</option>
          </select>
        </div>

        {isSuperAdmin && (
          <div className="filter-group export-group">
            <label>Export Category:</label>
            <select
              value={exportCategory}
              onChange={(e) => setExportCategory(e.target.value)}
              className="category-filter-select"
            >
              <option value="">All Categories</option>
              <option value="UserManagement">User Management</option>
              <option value="Authentication">Authentication</option>
              <option value="Upload_Events">Upload Events</option>
            </select>
            <button onClick={handleExport}>Export </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('timestamp')}>
                    Timestamp {sortConfig.key === 'timestamp' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('event_type')}>
                    Event Type {sortConfig.key === 'event_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('category')}>
                    Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Subcategory</th>
                  <th>User</th>
                  <th>Patient Name</th>
                  <th>Action</th>
                  <th>Details</th>
          </tr>
        </thead>
        <tbody>
                {paginatedLogs.map((log) => {
            const rawTimestamp = log.timestamp ?? log.timeStamp ?? log.details?.timestamp ?? log.details?.timeStamp ?? log.details?.time ?? log.time ?? log.created_at;
            const { action, browser, device, ip, location } = log.details || {};
            return (
              <tr key={log.id}>
                      <td>{formatTimestamp(rawTimestamp)}</td>
                      <td>{log.event_type}</td>
                      <td>{log.category}</td>
                      <td>{log.subCategory || ''}</td>

                      <td>{log.created_by || log.uploaded_by || log.admin_name || ''}</td>
                      <td>{log.patient_name || ''}</td>
                      <td>{action || ''}</td>
                      <td>
                        <details>
                          <summary>View Details</summary>
                          <div className="details-content">
                            <p>Browser: {browser || 'N/A'}</p>
                            <p>Device: {device || 'N/A'}</p>
                            <p>IP: {ip || 'N/A'}</p>
                            <p>Location: {location || 'N/A'}</p>
                            {log.success !== undefined && (
                              <p>Status: {log.success ? 'Success' : 'Failed'}</p>
                            )}
                            {log.failure_reason && (
                              <p>Failure Reason: {log.failure_reason}</p>
                            )}
                          </div>
                        </details>
                      </td>
              </tr>
            );
          })}
        </tbody>
      </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
